import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { IFormPage, IFormElement } from '../types';
import { FormField } from './FormField';
import { evaluateVisibility } from '../utils/visibility';
import styles from './components.module.css';

export interface FormPageProps {
  page: IFormPage;
  pageIndex: number;
  totalPages: number;
  questionStartNumber?: number;
  showQuestionNumbers?: boolean;
}

export const FormPage: React.FC<FormPageProps> = ({
  page,
  questionStartNumber = 1,
  showQuestionNumbers = true,
}) => {
  const { watch } = useFormContext();
  const formValues = watch();

  const isElementVisible = (element: IFormElement): boolean => {
    if (!element.visibleIf) return true;

    const fieldRegex = /\{([a-zA-Z_$][a-zA-Z0-9_$]*)\}/g;
    const matches = Array.from(element.visibleIf.matchAll(fieldRegex));
    const referencedFields = matches.map((match) => match[1]);

    const hasReferencedFieldValue = referencedFields.some((fieldName) => {
      const value = formValues[fieldName];

      if (value === undefined || value === null) return false;
      if (value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;

      return true;
    });

    if (!hasReferencedFieldValue) {
      return false;
    }

    return evaluateVisibility(element.visibleIf, formValues);
  };

  const visibleElements = useMemo(() => {
    return (page.elements || [])
      .map((element, originalIndex) => ({
        element,
        originalIndex,
        isVisible: isElementVisible(element),
      }))
      .filter((item) => item.isVisible);
  }, [page.elements, formValues]);

  return (
    <div>
      {(page.title || page.description) && (
        <div className={styles.pageHeader}>
          {page.description && (
            <p className={styles.pageDescription}>{page.description}</p>
          )}
        </div>
      )}

      <div className={styles.pageElements}>
        {visibleElements.map((item, visibleIndex) => (
          <React.Fragment key={item.element.name || item.originalIndex}>
            {visibleIndex > 0 && <hr className={styles.elementSeparator} />}
            <FormField
              element={item.element}
              questionNumber={
                showQuestionNumbers
                  ? questionStartNumber + visibleIndex
                  : undefined
              }
              totalQuestions={
                showQuestionNumbers ? visibleElements.length : undefined
              }
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default FormPage;
